import React, { useState, useEffect } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { getApiBaseUrl } from '../utils/apiBaseUrl'
import { getInventoryBadgeText, showsInInventory } from '../utils/carOptions'

interface Car {
  Year: number
  Model: string
  Miles: string
  ListingURL?: string
  Color: {
    Exterior: string
    Interior: string
  }
  Options: string[]
  Dealer: string
  Location: {
    City: string
    ZipCode: string
  }
  VIN: string
  Price: number
  Image: string
  Transmission: string
  BodyType?: string // "Convertible" or "Coupe"
  LeatherSeatsType?: string // "Full Leather", "Extended Leather", "Merino Leather", "Leather Seats", or empty
  LaserLightsType?: string // "Laser Lights" or empty
}

type SortField = 'price' | 'miles' | 'transmission' | 'year' | 'none'
type SortDirection = 'asc' | 'desc'
type TransmissionFilter = 'all' | 'manual' | 'auto'
type BodyTypeFilter = 'all' | 'convertible' | 'coupe'
type LeatherSeatsFilter = 'all' | 'full-leather' | 'extended-leather' | 'merino-leather' | 'leather-seats'
type LaserLightsFilter = 'all' | 'laser-lights'

interface ZoomedImage {
  src: string
  alt: string
}

const M8: React.FC = () => {
  const { user, isAuthenticated, loading: authLoading } = useAuth()
  const [cars, setCars] = useState<Car[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [lastUpdated, setLastUpdated] = useState<string | null>(null)
  const [streamingInProgress, setStreamingInProgress] = useState(false)
  const [sortField, setSortField] = useState<SortField>('none')
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc')
  const [zoomedImage, setZoomedImage] = useState<ZoomedImage | null>(null)
  const [transmissionFilter, setTransmissionFilter] = useState<TransmissionFilter>('all')
  const [bodyTypeFilter, setBodyTypeFilter] = useState<BodyTypeFilter>('all')
  const [minPrice, setMinPrice] = useState<string>('')
  const [maxPrice, setMaxPrice] = useState<string>('')
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false)
  const [selectedOptions, setSelectedOptions] = useState<Set<string>>(new Set())
  const [leatherSeatsFilter, setLeatherSeatsFilter] = useState<LeatherSeatsFilter>('all')
  const [laserLightsFilter, setLaserLightsFilter] = useState<LaserLightsFilter>('all')

  useEffect(() => {
    let eventSource: EventSource | null = null
    let isCleanedUp = false
    
    const fetchCars = () => {
      if (isCleanedUp) return null
      
      // Wait for auth to finish loading
      if (authLoading) {
        //console.log('Auth still loading, waiting...')
        return null
      }
      
      // Only fetch if authenticated
      if (!isAuthenticated) {
        //console.log('User not authenticated, cannot access M8 stream')
        setLoading(false)
        setStreamingInProgress(false)
        setError('Please sign in to view M8 inventory')
        return null
      }

      setLoading(true)
      setStreamingInProgress(true)
      setError(null)

      const apiUrl = getApiBaseUrl()
      
      // Get auth token for authenticated requests
      const token = localStorage.getItem('auth_token')
      const streamUrl = token 
        ? `${apiUrl}/api/stream/carbon-bucket-cars?token=${encodeURIComponent(token)}`
        : `${apiUrl}/api/stream/carbon-bucket-cars`
      
      //console.log('Connecting to API:', streamUrl)
      
      const eventSource = new EventSource(streamUrl)
      let hasReceivedCars = false
      const carBuffer: Car[] = []
      let bufferTimeout: NodeJS.Timeout | null = null
      
      const flushCarBuffer = () => {
        if (carBuffer.length > 0) {
          const carsToAdd = [...carBuffer].filter(car => car.Model === 'M8' && showsInInventory(car))
          carBuffer.length = 0
          
          setCars(prevCars => {
            if (prevCars.length === 0) {
              return carsToAdd
            }
            
            const existingVINs = new Set(prevCars.map(car => car.VIN))
            const newCars = carsToAdd.filter(car => car && car.VIN && !existingVINs.has(car.VIN))
            
            return [...prevCars, ...newCars]
          })
        }
      }
      
      eventSource.onopen = (event) => {
        //console.log('EventSource connection opened:', event)
      }
      
      eventSource.onmessage = (event) => {
        //console.log('Received EventSource message:', event.data)
        try {
          const data = JSON.parse(event.data)
          //console.log('Parsed data:', data)
          
          switch (data.type) {
            case 'status':
              //console.log('Stream status:', data.message)
              break
            
            case 'update_time':
              //console.log('Update time:', data.updated_at)
              setLastUpdated(data.updated_at)
              break
            
            case 'car':
              //console.log('Received car:', data.car.Year, data.car.Model, data.car.VIN)
              // Only add M8 cars to buffer
              if (data.car.Model === 'M8') {
                //console.log('Car data structure:', data.car)
                carBuffer.push(data.car)
                hasReceivedCars = true
                //console.log('M8 car buffer now has', carBuffer.length, 'cars')
                
                if (bufferTimeout) {
                  clearTimeout(bufferTimeout)
                }
                
                bufferTimeout = setTimeout(flushCarBuffer, 50)
              }
              break
            
            case 'initial_load_complete':
              //console.log('Initial load complete, flushing remaining M8 cars')
              flushCarBuffer()
              setLoading(false)
              setStreamingInProgress(false)
              //console.log('Initial M8 car load completed, keeping connection open for real-time updates')
              break
            
            case 'complete':
              //console.log('Stream complete (old API)')
              flushCarBuffer()
              setLoading(false)
              setStreamingInProgress(false)
              eventSource.close()
              //console.log('Stream completed - connection closed by server')
              
              setTimeout(() => {
                if (!isCleanedUp) {
                  //console.log('Reconnecting for updates...')
                  fetchCars()
                }
              }, 24 * 60 * 60 * 1000)
              break
          }
        } catch (err) {
          //console.error('Error parsing event data:', err)
        }
      }
      
      eventSource.onerror = (error) => {
        //console.error('EventSource error:', error)
        //console.error('EventSource readyState:', eventSource.readyState)
        //console.error('EventSource URL:', eventSource.url)
        
        const errorMessage = eventSource.readyState === EventSource.CLOSED 
          ? 'Connection to car stream closed. Retrying...'
          : 'Connection to car stream lost. Check your internet connection.'
        
        setError(errorMessage)
        setLoading(false)
        setStreamingInProgress(false)
        eventSource.close()
        
        setTimeout(() => {
          if (!isCleanedUp) {
            setError(null)
            fetchCars()
          }
        }, 3000)
      }

      return eventSource
    }

    eventSource = fetchCars()
    
    const interval = setInterval(() => {
      if (isCleanedUp) return
      //console.log('Interval refresh triggered')
      if (eventSource) {
        eventSource.close()
      }
      eventSource = fetchCars()
    }, 24 * 60 * 60 * 1000)
    
    return () => {
      isCleanedUp = true
      clearInterval(interval)
      if (eventSource) {
        eventSource.close()
      }
    }
  }, [isAuthenticated, authLoading])

  useEffect(() => {
    //console.log('State update - M8 cars:', cars.length, 'loading:', loading, 'streaming:', streamingInProgress, 'error:', error)
  }, [cars, loading, streamingInProgress, error])

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
    }).format(price)
  }

  const formatLastUpdated = (dateString: string) => {
    const date = new Date(dateString)
    return date.toLocaleDateString('en-US', {
      month: '2-digit',
      day: '2-digit',
      year: 'numeric'
    }) + ' ' + date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    })
  }

  const parseMiles = (miles: string): number => {
    return parseInt(miles.replace(/,/g, ''), 10)
  }

  // Extract unique options from all cars
  const getUniqueOptions = (): string[] => {
    const optionsSet = new Set<string>()
    cars.forEach(car => {
      if (car.Options && Array.isArray(car.Options)) {
        car.Options.forEach(option => {
          if (option && option.trim()) {
            optionsSet.add(option.trim())
          }
        })
      }
    })
    return Array.from(optionsSet).sort()
  }

  const toggleOption = (option: string) => {
    setSelectedOptions(prev => {
      const newSet = new Set(prev)
      if (newSet.has(option)) {
        newSet.delete(option)
      } else {
        newSet.add(option)
      }
      return newSet
    })
  }

  const getSortedCars = (): Car[] => {
    // First apply filters
    let filteredCars = [...cars]

    // Transmission filter
    if (transmissionFilter !== 'all') {
      filteredCars = filteredCars.filter(car => {
        if (!car.Transmission) return false
        const transmissionLower = car.Transmission.toLowerCase()
        if (transmissionFilter === 'manual') {
          return transmissionLower.includes('manual') && !transmissionLower.includes('auto')
        } else if (transmissionFilter === 'auto') {
          return transmissionLower.includes('auto') && !transmissionLower.includes('manual')
        }
        return true
      })
    }

    // Body type filter
    if (bodyTypeFilter !== 'all') {
      filteredCars = filteredCars.filter(car => {
        const bodyType = car.BodyType || 'Coupe' // Default to Coupe if not specified
        const bodyTypeLower = bodyType.toLowerCase()
        if (bodyTypeFilter === 'convertible') {
          return bodyTypeLower === 'convertible'
        } else if (bodyTypeFilter === 'coupe') {
          return bodyTypeLower === 'coupe'
        }
        return true
      })
    }

    // Leather Seats filter
    if (leatherSeatsFilter !== 'all') {
      filteredCars = filteredCars.filter(car => {
        if (!car.LeatherSeatsType) return false
        const leatherType = car.LeatherSeatsType.toLowerCase()
        if (leatherSeatsFilter === 'full-leather') {
          return leatherType === 'full leather'
        } else if (leatherSeatsFilter === 'extended-leather') {
          return leatherType === 'extended leather'
        } else if (leatherSeatsFilter === 'merino-leather') {
          return leatherType === 'merino leather'
        } else if (leatherSeatsFilter === 'leather-seats') {
          return leatherType === 'leather seats'
        }
        return true
      })
    }

    // Laser Lights filter
    if (laserLightsFilter !== 'all') {
      filteredCars = filteredCars.filter(car => {
        if (!car.LaserLightsType) return false
        return car.LaserLightsType.toLowerCase() === 'laser lights'
      })
    }

    // Options filter (car must have ALL selected options)
    if (selectedOptions.size > 0) {
      filteredCars = filteredCars.filter(car => {
        if (!car.Options || !Array.isArray(car.Options)) return false
        const carOptions = new Set(car.Options.map(opt => opt.trim()))
        // Check if car has all selected options
        return Array.from(selectedOptions).every(selectedOpt => carOptions.has(selectedOpt))
      })
    }

    // Price range filter
    const minPriceNum = minPrice ? parseFloat(minPrice) : 0
    const maxPriceNum = maxPrice ? parseFloat(maxPrice) : Infinity

    if (minPrice || maxPrice) {
      filteredCars = filteredCars.filter(car => {
        const price = car.Price === 0 ? Infinity : car.Price
        return price >= minPriceNum && price <= maxPriceNum
      })
    }

    // Then apply sorting
    if (sortField === 'none') return filteredCars;

    const sortedCars = [...filteredCars].sort((a, b) => {
      let comparison = 0

      switch (sortField) {
        case 'price':
          const priceA = a.Price === 0 ? Infinity : a.Price
          const priceB = b.Price === 0 ? Infinity : b.Price
          comparison = priceA - priceB
          break
        case 'miles':
          comparison = parseMiles(a.Miles) - parseMiles(b.Miles)
          break
        case 'transmission':
          const transA = a.Transmission === 'Manual' ? 0 : 1
          const transB = b.Transmission === 'Manual' ? 0 : 1
          comparison = transA - transB
          break
        case 'year':
          comparison = a.Year - b.Year
          break
      }

      return sortDirection === 'asc' ? comparison : -comparison
    })

    return sortedCars
  }

  const handleSort = (field: SortField) => {
    if (field === sortField) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')
    } else {
      setSortField(field)
      setSortDirection('asc')
    }
  }

  const LoadingState = () => (
    <div className="flex items-center justify-center min-h-[400px]">
      <div className="text-center space-y-4">
        <div className="inline-flex items-center justify-center w-16 h-16 bg-primary-100 dark:bg-primary-900/20 rounded-full">
          <div className="w-8 h-8 border-3 border-primary-600 border-t-transparent rounded-full animate-spin"></div>
        </div>
        <div className="space-y-2">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
            {loading ? 'Loading M8 cars...' : `Streaming M8 cars...`}
          </h3>
          <p className="text-gray-600 dark:text-gray-400">
            {loading ? 'Fetching the latest M8 inventory' : `${cars.length} M8 cars loaded`}
          </p>
        </div>
      </div>
    </div>
  )

  const EmptyState = () => (
    <div className="flex items-center justify-center min-h-[400px]">
      <div className="text-center space-y-4">
        <div className="inline-flex items-center justify-center w-16 h-16 bg-gray-100 dark:bg-gray-800 rounded-full">
          <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </div>
        <div className="space-y-2">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">No M8 cars found</h3>
          <p className="text-gray-600 dark:text-gray-400">Check back soon for new M8 inventory</p>
        </div>
      </div>
    </div>
  )

  return (
    <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Hero Section */}
      <div className="mb-8 space-y-4">
        <div className="space-y-2">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white">
            BMW M8 (F9X)
          </h2>
          <h3 className="text-xl md:text-2xl text-gray-600 dark:text-gray-300">
            with Carbon Bucket Seats
          </h3>
        </div>
        <p className="text-gray-600 dark:text-gray-400 max-w-2xl">
          Real-time M8 inventory from dealerships nationwide. Updated every hour.
        </p>
        {lastUpdated && (
          <div className="text-sm text-gray-500 dark:text-gray-400">
            Last updated: {formatLastUpdated(lastUpdated)}
          </div>
        )}
      </div>

      {/* Error State */}
      {error && (
        <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
          <div className="flex items-center space-x-2">
            <svg className="w-5 h-5 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <p className="text-red-800 dark:text-red-200 text-sm">{error}</p>
          </div>
        </div>
      )}

      {/* Loading or Empty State */}
      {(authLoading || loading || streamingInProgress) && <LoadingState />}
      {!authLoading && !loading && !streamingInProgress && cars.length === 0 && <EmptyState />}

      {/* Content */}
      {cars.length > 0 && (
        <div className="space-y-6">
          {/* Header with Filter Button */}
          <div className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-800 shadow-sm">
            <div className="p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div className="flex items-center gap-3">
                <span className="text-sm text-gray-600 dark:text-gray-400">
                  <span className="font-semibold text-gray-900 dark:text-white">{getSortedCars().length}</span> M8 cars
                </span>
                {(loading || streamingInProgress) && (
                  <div className="flex items-center gap-1.5">
                    <div className="w-1.5 h-1.5 bg-primary-500 rounded-full animate-pulse"></div>
                    <span className="text-xs text-primary-600 dark:text-primary-400">Updating</span>
                  </div>
                )}
              </div>

              <div className="flex items-center gap-3">
                {/* Active Filter Badges */}
                <div className="flex flex-wrap items-center gap-1.5">
                  {sortField !== 'none' && (
                    <span className="px-2 py-1 text-xs bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 rounded">
                      Sort: {sortField.charAt(0).toUpperCase() + sortField.slice(1)} {sortDirection === 'asc' ? '↑' : '↓'}
                    </span>
                  )}
                  {transmissionFilter !== 'all' && (
                    <span className="px-2 py-1 text-xs bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 rounded">
                      {transmissionFilter === 'manual' ? 'Manual' : 'Auto'}
                    </span>
                  )}
                  {bodyTypeFilter !== 'all' && (
                    <span className="px-2 py-1 text-xs bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded">
                      {bodyTypeFilter === 'convertible' ? 'Convertible' : 'Coupe'}
                    </span>
                  )}
                  {(minPrice || maxPrice) && (
                    <span className="px-2 py-1 text-xs bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 rounded">
                      ${minPrice || '0'} - ${maxPrice || '∞'}
                    </span>
                  )}
                  {selectedOptions.size > 0 && (
                    <span className="px-2 py-1 text-xs bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300 rounded">
                      {selectedOptions.size} option{selectedOptions.size > 1 ? 's' : ''}
                    </span>
                  )}
                  {leatherSeatsFilter !== 'all' && (
                    <span className="px-2 py-1 text-xs bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300 rounded">
                      {leatherSeatsFilter === 'full-leather' ? 'Full Leather' :
                       leatherSeatsFilter === 'extended-leather' ? 'Extended Leather' :
                       leatherSeatsFilter === 'merino-leather' ? 'Merino Leather' :
                       'Leather Seats'}
                    </span>
                  )}
                  {laserLightsFilter !== 'all' && (
                    <span className="px-2 py-1 text-xs bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 rounded">
                      Laser Lights
                    </span>
                  )}
                </div>

                {/* Filter Button */}
                <button
                  onClick={() => setIsFilterModalOpen(true)}
                  className="px-4 py-2 text-sm font-medium bg-primary-600 hover:bg-primary-700 text-white rounded-lg transition-colors flex items-center gap-2"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
                  </svg>
                  Sort & Filter
                </button>
              </div>
            </div>
          </div>

          {/* Filter Modal */}
          {isFilterModalOpen && (
            <div className="fixed inset-0 z-50 overflow-y-auto">
              <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:p-0">
                {/* Backdrop */}
                <div
                  className="fixed inset-0 bg-gray-500 dark:bg-gray-900 bg-opacity-75 dark:bg-opacity-75 transition-opacity"
                  onClick={() => setIsFilterModalOpen(false)}
                />

                {/* Modal */}
                <div className="relative inline-block align-bottom bg-white dark:bg-gray-800 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
                  {/* Header */}
                  <div className="bg-gradient-to-r from-primary-600 to-primary-700 px-6 py-4">
                    <div className="flex items-center justify-between">
                      <h3 className="text-lg font-semibold text-white">Sort & Filter</h3>
                      <button
                        onClick={() => setIsFilterModalOpen(false)}
                        className="text-white hover:text-gray-200 transition-colors"
                      >
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                  </div>

                  {/* Content */}
                  <div className="px-6 py-4 space-y-6">
                    {/* Sort By Section */}
                    <div>
                      <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
                        </svg>
                        Sort By
                      </h4>
                      <div className="grid grid-cols-3 gap-2">
                        {[
                          { field: 'price' as SortField, label: 'Price', icon: '$' },
                          { field: 'miles' as SortField, label: 'Miles', icon: '↗' },
                          { field: 'year' as SortField, label: 'Year', icon: '📅' },
                        ].map(({ field, label, icon }) => (
                          <button
                            key={field}
                            onClick={() => handleSort(field)}
                            className={`p-3 rounded-lg border-2 transition-all ${
                              sortField === field
                                ? 'border-primary-600 bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300'
                                : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:border-primary-300 dark:hover:border-primary-700'
                            }`}
                          >
                            <div className="text-lg mb-1">{icon}</div>
                            <div className="text-xs font-medium">{label}</div>
                            {sortField === field && (
                              <div className="text-xs mt-1 text-primary-600 dark:text-primary-400">
                                {sortDirection === 'asc' ? '↑ Low to High' : '↓ High to Low'}
                              </div>
                            )}
                          </button>
                        ))}
                      </div>
                      {sortField !== 'none' && (
                        <button
                          onClick={() => setSortField('none')}
                          className="mt-2 text-xs text-gray-600 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400"
                        >
                          Clear sort
                        </button>
                      )}
                    </div>

                    {/* Divider */}
                    <div className="border-t border-gray-200 dark:border-gray-700" />

                    {/* Filters Section */}
                    <div>
                      <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
                        </svg>
                        Filters
                      </h4>

                      <div className="space-y-4">
                        {/* Transmission Filter */}
                        <div>
                          <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Transmission
                          </label>
                          <div className="flex gap-2">
                            {[
                              { value: 'all' as TransmissionFilter, label: 'All' },
                              { value: 'manual' as TransmissionFilter, label: 'Manual' },
                              { value: 'auto' as TransmissionFilter, label: 'Auto' },
                            ].map(({ value, label }) => (
                              <button
                                key={value}
                                onClick={() => setTransmissionFilter(value)}
                                className={`flex-1 px-4 py-2 text-sm rounded-lg transition-all ${
                                  transmissionFilter === value
                                    ? 'bg-green-600 text-white'
                                    : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                                }`}
                              >
                                {label}
                              </button>
                            ))}
                          </div>
                        </div>

                        {/* Body Type Filter */}
                        <div>
                          <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Body Type
                          </label>
                          <div className="flex gap-2">
                            {[
                              { value: 'all' as BodyTypeFilter, label: 'All' },
                              { value: 'coupe' as BodyTypeFilter, label: 'Coupe' },
                              { value: 'convertible' as BodyTypeFilter, label: 'Convertible' },
                            ].map(({ value, label }) => (
                              <button
                                key={value}
                                onClick={() => setBodyTypeFilter(value)}
                                className={`flex-1 px-4 py-2 text-sm rounded-lg transition-all ${
                                  bodyTypeFilter === value
                                    ? 'bg-blue-600 text-white'
                                    : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                                }`}
                              >
                                {label}
                              </button>
                            ))}
                          </div>
                        </div>

                        {/* Price Range Filter */}
                        <div>
                          <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Price Range
                          </label>
                          <div className="flex gap-3 items-center">
                            <div className="flex-1">
                              <div className="relative">
                                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-gray-500">$</span>
                                <input
                                  type="number"
                                  placeholder="Min"
                                  value={minPrice}
                                  onChange={(e) => setMinPrice(e.target.value)}
                                  className="w-full pl-7 pr-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                                />
                              </div>
                            </div>
                            <span className="text-gray-400">—</span>
                            <div className="flex-1">
                              <div className="relative">
                                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-gray-500">$</span>
                                <input
                                  type="number"
                                  placeholder="Max"
                                  value={maxPrice}
                                  onChange={(e) => setMaxPrice(e.target.value)}
                                  className="w-full pl-7 pr-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                                />
                              </div>
                            </div>
                          </div>
                          {(minPrice || maxPrice) && (
                            <button
                              onClick={() => {
                                setMinPrice('')
                                setMaxPrice('')
                              }}
                              className="mt-2 text-xs text-gray-600 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400"
                            >
                              Clear price range
                            </button>
                          )}
                        </div>

                        {/* Options Filter */}
                        <div>
                          <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Options & Packages
                          </label>
                          <div className="max-h-64 overflow-y-auto space-y-2 border border-gray-200 dark:border-gray-700 rounded-lg p-3 bg-gray-50 dark:bg-gray-800/50">
                            {getUniqueOptions().length > 0 ? (
                              getUniqueOptions().map((option) => (
                                <label
                                  key={option}
                                  className="flex items-center space-x-2 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 p-2 rounded transition-colors"
                                >
                                  <input
                                    type="checkbox"
                                    checked={selectedOptions.has(option)}
                                    onChange={() => toggleOption(option)}
                                    className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-700"
                                  />
                                  <span className="text-sm text-gray-700 dark:text-gray-300 flex-1">
                                    {option}
                                  </span>
                                </label>
                              ))
                            ) : (
                              <div className="text-xs text-gray-500 dark:text-gray-400 text-center py-4">
                                No options available
                              </div>
                            )}
                          </div>
                          {selectedOptions.size > 0 && (
                            <button
                              onClick={() => setSelectedOptions(new Set())}
                              className="mt-2 text-xs text-gray-600 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400"
                            >
                              Clear all options
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Footer */}
                  <div className="bg-gray-50 dark:bg-gray-900 px-6 py-4 flex justify-between items-center">
                    <button
                      onClick={() => {
                        setSortField('none')
                        setTransmissionFilter('all')
                        setBodyTypeFilter('all')
                        setMinPrice('')
                        setMaxPrice('')
                        setSelectedOptions(new Set())
                        setLeatherSeatsFilter('all')
                        setLaserLightsFilter('all')
                      }}
                      className="text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
                    >
                      Reset All
                    </button>
                    <button
                      onClick={() => setIsFilterModalOpen(false)}
                      className="px-6 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg transition-colors text-sm font-medium"
                    >
                      Apply
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Desktop Table */}
          <div className="hidden xl:block bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-800 overflow-hidden shadow-sm">
            <table className="w-full">
              <thead className="bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                    Vehicle
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                    Image
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                    Miles
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                    Trans
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                    Location
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                    Colors
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                    Price
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {getSortedCars().map((car, index) => (
                  <tr key={car.VIN} className="hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors">
                    <td className="px-4 py-4">
                      <div className="space-y-1">
                        <div className="font-semibold text-gray-900 dark:text-white">
                          {car.Year} {car.Model}
                        </div>
                        <div className="inline-flex items-center px-2 py-1 text-xs font-medium bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300 rounded-full">
                          ✓ {getInventoryBadgeText(car)}
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400 font-mono">
                          {car.VIN}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-4">
                      {car.Image ? (
                        <img 
                          src={car.Image} 
                          alt={`${car.Year} ${car.Model}`}
                          className="w-20 h-14 object-cover rounded-lg border border-gray-200 dark:border-gray-700 cursor-pointer hover:opacity-80 transition-opacity shadow-sm"
                          onClick={() => setZoomedImage({ src: car.Image, alt: `${car.Year} ${car.Model}` })}
                          onError={(e) => {
                            e.currentTarget.src = '';
                          }}
                        />
                      ) : (
                        <div className="w-20 h-14 bg-gray-100 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 flex items-center justify-center">
                          <svg className="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                          </svg>
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-4">
                      <div className="text-sm font-medium text-gray-900 dark:text-white">
                        {car.Miles}
                      </div>
                    </td>
                    <td className="px-4 py-4">
                      <div className="text-sm text-gray-900 dark:text-white">
                        {car.Transmission === 'Manual' ? 'Manual' : 'Auto'}
                      </div>
                    </td>
                    <td className="px-4 py-4">
                      <div className="text-sm text-gray-900 dark:text-white">
                        {car.Location.City}
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">
                        {car.Location.ZipCode}
                      </div>
                    </td>
                    <td className="px-4 py-4">
                      <div className="space-y-1">
                        <div className="text-sm text-gray-900 dark:text-white">
                          {car.Color.Exterior}
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">
                          {car.Color.Interior}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-4">
                      <div className="text-sm font-semibold text-gray-900 dark:text-white">
                        {car.Price > 0 ? formatPrice(car.Price) : 'Contact Dealer'}
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">
                        {car.Dealer}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile Cards */}
          <div className="xl:hidden space-y-4">
            {getSortedCars().map((car, index) => (
              <div key={car.VIN} className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-800 p-4 shadow-sm">
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0">
                    {car.Image ? (
                      <img 
                        src={car.Image} 
                        alt={`${car.Year} ${car.Model}`}
                        className="w-24 h-16 object-cover rounded-lg border border-gray-200 dark:border-gray-700 cursor-pointer hover:opacity-80 transition-opacity shadow-sm"
                        onClick={() => setZoomedImage({ src: car.Image, alt: `${car.Year} ${car.Model}` })}
                        onError={(e) => {
                          e.currentTarget.src = '';
                        }}
                      />
                    ) : (
                      <div className="w-24 h-16 bg-gray-100 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 flex items-center justify-center">
                        <svg className="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                      </div>
                    )}
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between">
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                          {car.Year} {car.Model}
                        </h3>
                        <div className="inline-flex items-center px-2 py-1 mt-1 text-xs font-medium bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300 rounded-full">
                          ✓ {getInventoryBadgeText(car)}
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-lg font-bold text-gray-900 dark:text-white">
                          {car.Price > 0 ? formatPrice(car.Price) : 'Contact Dealer'}
                        </div>
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4 text-sm mt-4">
                      <div>
                        <span className="text-gray-500 dark:text-gray-400 block mb-1">Miles</span>
                        <div className="font-semibold text-gray-900 dark:text-white">{car.Miles}</div>
                      </div>
                      <div>
                        <span className="text-gray-500 dark:text-gray-400 block mb-1">Transmission</span>
                        <div className="font-semibold text-gray-900 dark:text-white">{car.Transmission || 'Unknown'}</div>
                      </div>
                      <div>
                        <span className="text-gray-500 dark:text-gray-400 block mb-1">Location</span>
                        <div className="font-semibold text-gray-900 dark:text-white">{car.Location.City}</div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">{car.Location.ZipCode}</div>
                      </div>
                      <div>
                        <span className="text-gray-500 dark:text-gray-400 block mb-1">Colors</span>
                        <div className="font-semibold text-gray-900 dark:text-white">{car.Color.Exterior}</div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">{car.Color.Interior}</div>
                      </div>
                    </div>
                    
                    <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                      <div className="text-sm">
                        <span className="text-gray-500 dark:text-gray-400 block mb-1">Dealer</span>
                        <div className="font-medium text-gray-900 dark:text-white">{car.Dealer}</div>
                      </div>
                      <div className="mt-2 text-xs">
                        <span className="text-gray-500 dark:text-gray-400 block mb-1">VIN</span>
                        <div className="font-mono text-gray-700 dark:text-gray-300">{car.VIN}</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Image Zoom Modal */}
      {zoomedImage && (
        <div 
          className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4 backdrop-blur-sm"
          onClick={() => setZoomedImage(null)}
        >
          <div className="relative w-full h-full max-w-4xl max-h-full flex items-center justify-center">
            <button
              onClick={() => setZoomedImage(null)}
              className="absolute top-4 right-4 text-white bg-black/50 rounded-full p-2 hover:bg-black/75 transition-all z-10"
              aria-label="Close image"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
            <img 
              src={zoomedImage.src} 
              alt={zoomedImage.alt}
              className="max-w-[calc(100vw-2rem)] max-h-[calc(100vh-2rem)] object-contain rounded-lg"
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        </div>
      )}
    </main>
  )
}

export default M8 